import React, { useEffect } from "react";
import { Button, CustomSelect, Input } from "components";
import { CompanyInfoData } from "./types";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { GalleryUploadIcon } from "assets";

const companySchema = yup.object({
  name: yup.string().required("Required"),
  industry: yup.string().required("Required"),
  frequency: yup.object({
    label: yup.string().required("Required"),
    value: yup.string().required("Required"),
  }),
  employees: yup.object({
    label: yup.string().required("Required"),
    value: yup.string().required("Required"),
  }),
  company_logo: yup.string().notRequired().default(undefined),
}) as yup.ObjectSchema<CompanyInfoData>;

const frequencyOptions = [
  { label: "1—10", value: "1-10" },
  { label: "11—50", value: "11-50" },
  { label: "51—100", value: "51-100" },
  { label: "100+", value: "100+" },
];

const employeeOptions = [
  { label: "1—10", value: "1-10" },
  { label: "11—50", value: "11-50" },
  { label: "51—200", value: "51-200" },
  { label: "200+", value: "200+" },
];

interface CompanyInfoProps {
  submit: (data: CompanyInfoData) => void;
  goBack: () => void;
  handleImageUpload: () => void;
  image: string | null;
  imageURL?: string | null;
  existingLogoURL?: string | null;
  isSubmitting?: boolean;
}

export const CompanyInfoForm: React.FC<CompanyInfoProps> = ({
  submit,
  goBack,
  handleImageUpload,
  image,
  imageURL,
  existingLogoURL,
  isSubmitting,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<CompanyInfoData>({
    resolver: yupResolver(companySchema),
    mode: "onTouched",
    defaultValues: {
      name: "",
      industry: "",
      frequency: { label: "", value: "" },
      employees: { label: "", value: "" },
      company_logo: "",
    },
  });

  const displayLogoURL = imageURL || existingLogoURL;

  useEffect(() => {
    if (image) {
      setValue("company_logo", image, { shouldValidate: true });
    }
  }, [image, setValue]);

  return (
    <form className="max-h-[380px] overflow-y-auto scrollbar-none">
      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          Company Logo
        </label>
        <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-white border border-gray-300 flex items-center justify-center overflow-hidden">
          {displayLogoURL ? (
            <img
              src={displayLogoURL}
              alt="Company Logo"
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className="text-[#012169] font-semibold text-xl select-none">
              {(watch("name") || "")
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((w) => w[0].toUpperCase())
                .join("") || ""}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={handleImageUpload}
          className="px-4 py-2 !ml-0 border border-[#012169] text-[#012169] rounded-md text-sm font-medium bg-white hover:bg-blue-50 transition-colors flex items-center gap-2"
        >
          <GalleryUploadIcon className="w-4 h-4" />
          Upload company Logo
        </button>
      </div>
      </div>

      <div className="mb-6">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          Company Name <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder="Precision Yudimy"
          type="text"
          required
          validatorMessage={errors.name?.message}
          name="name"
          register={register}
          value={watch("name")}
          showRequired
        />
      </div>

      <div className="mb-6">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          Which industry best describes your organization? <span className="text-red-500">*</span>
        </label>
        <Input
          placeholder="Creative, Agriculture"
          type="text"
          required
          validatorMessage={errors.industry?.message}
          name="industry"
          register={register}
          value={watch("industry")}
          showRequired
        />
      </div>

      <div className="mb-6">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-2.5 font-medium block">
          Annual Hiring Frequency
        </label>
        <CustomSelect
          placeholder="1—10"
          options={frequencyOptions}
          onChange={(val) => setValue("frequency", val)}
          validatorMessage={errors.frequency?.value?.message?.toString() ?? ""}
          name="frequency"
          value={watch("frequency")}
          showRequired
        />
      </div>

      <div className="mb-8">
        <label className="text-sm bg-transparent p-0 text-gray-600 mb-4 font-medium block">
          Number of Employees
        </label>
        <div className="grid grid-cols-4 gap-4">
          {employeeOptions.map((item, index) => (
            <button
              key={index}
              type="button"
              className={`
                rounded-md border p-4 text-center transition-colors
                ${watch("employees")?.value === item.value
                  ? 'bg-blue-100 text-[#012169] border-blue-300'
                  : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                }
              `}
              onClick={() => setValue("employees", item, { shouldValidate: true })}
            >
              <div className="font-semibold text-lg mb-1">
                {item.label}
              </div>
              <div className="text-sm text-gray-500">
                Employees
              </div>
            </button>
          ))}
        </div>
        {errors.employees?.value?.message && (
          <p className="text-xs text-red-500 block mt-2">
            {errors.employees?.value?.message?.toString()}
          </p>
        )}
      </div>

      <div className="flex gap-4">
        <Button
          className="max-w-sm flex-1 border !border-[#012169] !text-[#012169] bg-white"
          type="outline"
          onClick={goBack}
        >
          Back
        </Button>
        <Button
          className="max-w-sm flex-1 !bg-[#012169] !border-none text-white"
          type="fill"
          onClick={handleSubmit(submit)}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Completing Setup..." : "Complete Setup"}
        </Button>
      </div>
    </form>
  );
};
